import type { Snowflake } from "../types";
import {
  WORLD_W,
  WORLD_H,
  NUM_SNOWFLAKES,
  SNOWFLAKE_MIN_SIZE,
  SNOWFLAKE_MAX_SIZE,
  SNOWFLAKE_MIN_SPEED,
  SNOWFLAKE_MAX_SPEED,
  SNOWFLAKE_DRIFT_RANGE,
  SNOWFLAKE_MIN_OPACITY,
  SNOWFLAKE_MAX_OPACITY,
  SNOWFLAKE_MIN_LIFETIME,
  SNOWFLAKE_MAX_LIFETIME,
  SNOWFLAKE_FADE_TIME,
} from "../constants";

export const initializeSnowflakes = (): Snowflake[] => {
  const snowflakes: Snowflake[] = [];
  const initialTime = performance.now();
  for (let i = 0; i < NUM_SNOWFLAKES; i++) {
    const lifetime = SNOWFLAKE_MIN_LIFETIME + Math.random() * (SNOWFLAKE_MAX_LIFETIME - SNOWFLAKE_MIN_LIFETIME);
    snowflakes.push({
      x: Math.random() * WORLD_W,
      y: Math.random() * WORLD_H,
      size: SNOWFLAKE_MIN_SIZE + Math.random() * (SNOWFLAKE_MAX_SIZE - SNOWFLAKE_MIN_SIZE),
      speed: SNOWFLAKE_MIN_SPEED + Math.random() * (SNOWFLAKE_MAX_SPEED - SNOWFLAKE_MIN_SPEED),
      drift: (Math.random() - 0.5) * SNOWFLAKE_DRIFT_RANGE,
      opacity: SNOWFLAKE_MIN_OPACITY + Math.random() * (SNOWFLAKE_MAX_OPACITY - SNOWFLAKE_MIN_OPACITY),
      spawnTime: initialTime - Math.random() * lifetime, // Stagger initial spawn times
      lifetime: lifetime,
    });
  }
  return snowflakes;
};

export const updateSnowflake = (flake: Snowflake, dt: number, now: number): void => {
  // Check if snowflake should disappear (lifetime expired)
  const elapsed = now - flake.spawnTime;
  if (elapsed >= flake.lifetime) {
    // Reset snowflake to random position anywhere in world with new lifetime
    flake.x = Math.random() * WORLD_W;
    flake.y = Math.random() * WORLD_H;
    flake.spawnTime = now;
    flake.lifetime = SNOWFLAKE_MIN_LIFETIME + Math.random() * (SNOWFLAKE_MAX_LIFETIME - SNOWFLAKE_MIN_LIFETIME);
    flake.size = SNOWFLAKE_MIN_SIZE + Math.random() * (SNOWFLAKE_MAX_SIZE - SNOWFLAKE_MIN_SIZE);
    flake.speed = SNOWFLAKE_MIN_SPEED + Math.random() * (SNOWFLAKE_MAX_SPEED - SNOWFLAKE_MIN_SPEED);
    flake.drift = (Math.random() - 0.5) * SNOWFLAKE_DRIFT_RANGE;
    flake.opacity = SNOWFLAKE_MIN_OPACITY + Math.random() * (SNOWFLAKE_MAX_OPACITY - SNOWFLAKE_MIN_OPACITY);
  }

  // Update position in world space
  flake.y += flake.speed * dt;
  flake.x += flake.drift * dt;

  // Wrap around world edges
  if (flake.y > WORLD_H + 100) {
    flake.y = -100;
    flake.x = Math.random() * WORLD_W;
  }
  if (flake.y < -100) {
    flake.y = WORLD_H + 100;
    flake.x = Math.random() * WORLD_W;
  }
  if (flake.x < -100) {
    flake.x = WORLD_W + 100;
  } else if (flake.x > WORLD_W + 100) {
    flake.x = -100;
  }
};

export const getSnowflakeOpacity = (flake: Snowflake, now: number): number => {
  const elapsed = now - flake.spawnTime;
  const timeRemaining = flake.lifetime - elapsed;
  const fadeTime = SNOWFLAKE_FADE_TIME;
  let displayOpacity = flake.opacity;
  if (timeRemaining < fadeTime) {
    displayOpacity = flake.opacity * (timeRemaining / fadeTime);
  }
  return displayOpacity;
};

export const drawSnowflake = (
  ctx: CanvasRenderingContext2D,
  flake: Snowflake,
  screenX: number,
  screenY: number,
  opacity: number
): void => {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset to screen coordinates for drawing
  ctx.globalAlpha = opacity;
  ctx.fillStyle = "#ffffff";
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 1;

  // Draw a simple snowflake shape (cross pattern)
  const size = flake.size;
  ctx.beginPath();
  // Horizontal line
  ctx.moveTo(screenX - size * 2, screenY);
  ctx.lineTo(screenX + size * 2, screenY);
  // Vertical line
  ctx.moveTo(screenX, screenY - size * 2);
  ctx.lineTo(screenX, screenY + size * 2);
  // Diagonal lines
  ctx.moveTo(screenX - size * 1.4, screenY - size * 1.4);
  ctx.lineTo(screenX + size * 1.4, screenY + size * 1.4);
  ctx.moveTo(screenX - size * 1.4, screenY + size * 1.4);
  ctx.lineTo(screenX + size * 1.4, screenY - size * 1.4);
  ctx.stroke();

  // Add a small circle in the center
  ctx.beginPath();
  ctx.arc(screenX, screenY, size * 0.5, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
};

